/**
 * Notification Context
 * Provides notification state and functions globally via React Context
 * Supports both HTTP and WebSocket modes
 * 
 * Modes:
 * - 'http': HTTP-only mode (uses useNotificationsHTTP)
 * - 'websocket': WebSocket-only mode (uses useUniversalNotifications)
 * - 'hybrid': HTTP for list, WebSocket for toasts (default: 'http')
 */

import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useUniversalNotifications } from '../../hooks/useUniversalNotifications';
import { useNotificationsHTTP } from '../../hooks/useNotificationsHTTP';
import { useAuth } from '../../assets/utils/useAuth';

// Create context with default values to prevent errors
const NotificationContext = createContext({
  connected: false,
  unreadCount: 0,
  notifications: [],
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  deleteNotification: async () => {},
  clearNotifications: async () => {},
  refreshNotifications: () => [],
  mode: 'http', // 'http' | 'websocket' | 'hybrid'
});

/**
 * Provider component for notification context
 * Uses auth context to reactively connect when user logs in
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} props.mode - 'http' | 'websocket' | 'hybrid' (default: 'http')
 * @param {boolean} props.enableWebSocketToasts - Enable WebSocket for toasts in hybrid mode (default: false)
 */
export const NotificationProvider = ({ 
  children, 
  mode = 'http',
  enableWebSocketToasts = false,
}) => {
  // Get token and user from auth context (reacts to login/logout)
  const { token, user } = useAuth();

  // Extract token from auth context
  const authToken = useMemo(() => {
    if (token) return token;
    if (user?.jwt) return user.jwt;
    if (user?.token) return user.token;
    if (user?.access_token) return user.access_token;
    return null;
  }, [token, user]);

  // Extract user ID from auth context
  const userId = useMemo(() => {
    if (user?.id) return user.id;
    if (user?.user_id) return user.user_id;
    if (user?.sub) return user.sub;
    if (user?.staffId) return user.staffId;
    return null;
  }, [user]);

  const isEnabled = !!authToken;
  
  // Debug logging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔔 NotificationProvider State:', {
        mode,
        hasToken: !!authToken,
        hasUser: !!user,
        enabled: isEnabled,
        userId: userId,
      });
    }
  }, [authToken, user, isEnabled, userId, mode]);

  // HTTP mode: Use HTTP hook for notifications
  const httpNotifications = useNotificationsHTTP({
    token: authToken,
    enabled: isEnabled && (mode === 'http' || mode === 'hybrid'),
    refreshInterval: 30000, // 30s polling
    pageSize: 20,
  });

  // WebSocket mode: Use WebSocket hook for notifications
  const wsNotifications = useUniversalNotifications({
    enabled: isEnabled && (mode === 'websocket' || (mode === 'hybrid' && enableWebSocketToasts)),
    token: authToken || '',
    userId: userId || undefined,
  });

  // Determine which hook to use based on mode
  const useHTTP = mode === 'http' || (mode === 'hybrid' && !enableWebSocketToasts);
  const activeNotifications = useHTTP ? httpNotifications : wsNotifications;

  // Merge context value based on active mode
  const contextValue = useMemo(() => {
    if (useHTTP) {
      // HTTP mode
      return {
        connected: true, // HTTP is always "connected"
        unreadCount: httpNotifications.unreadCount || 0,
        notifications: httpNotifications.notifications || [],
        markAsRead: httpNotifications.markAsRead,
        markAllAsRead: httpNotifications.markAllAsRead,
        deleteNotification: httpNotifications.deleteNotification,
        refreshNotifications: httpNotifications.refresh,
        clearNotifications: async () => {
          // HTTP mode doesn't have clearNotifications, so we'll mark all as read
          await httpNotifications.markAllAsRead();
        },
        mode: 'http',
      };
    } else {
      // WebSocket mode
      return {
        connected: wsNotifications.connected || false,
        unreadCount: wsNotifications.unreadCount || 0,
        notifications: wsNotifications.notifications || [],
        markAsRead: wsNotifications.markAsRead,
        markAllAsRead: wsNotifications.markAllAsRead,
        clearNotifications: wsNotifications.clearNotifications,
        refreshNotifications: wsNotifications.refreshNotifications,
        mode: 'websocket',
      };
    }
  }, [useHTTP, httpNotifications, wsNotifications]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Hook to use notification context
 * Always returns a valid context object (defaults if outside provider)
 */
export const useNotificationContext = () => {
  // useContext will return the default value if outside provider,
  // or the provider's value if inside provider
  return useContext(NotificationContext);
};

